import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { FaCreditCard, FaLock, FaPhone, FaEnvelope } from 'react-icons/fa';
import { placeholderImage } from '../utils/placeholderImage';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createPaymentIntent, createOrderInFirebase } from '../services/firebaseFunctions';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Payment Form Component
const PaymentForm = ({ subtotal, total, formData, items, orderType, onSuccess, onError, isProcessing, setIsProcessing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentStatus, setPaymentStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setPaymentStatus('Creating payment...');

    try {
      // Create payment intent using Firebase Functions
      console.log('Sending payment request to Firebase Functions...');
      console.log('Subtotal:', subtotal);
      console.log('Total:', total);
      
      const responseData = await createPaymentIntent(
        { subtotal: subtotal },
        items,
        {
          ...formData,
          orderType: orderType
        }
      );

      console.log('Response data:', responseData);
      
      if (responseData.warning) {
        console.log('Payment warning:', responseData.warning);
        setPaymentStatus(responseData.warning);
      }
      
      const { clientSecret } = responseData;

      setPaymentStatus('Processing payment...');

      // Confirm payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: formData.firstName + ' ' + formData.lastName,
            email: formData.email
          }
        }
      });

      if (error) {
        setPaymentStatus('');
        onError(error.message);
      } else if (paymentIntent.status === 'succeeded') {
        setPaymentStatus('Payment successful!');
        onSuccess(paymentIntent);
      }
    } catch (error) {
      setPaymentStatus('');
      onError('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
          Card Details
        </label>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
            hidePostalCode: true,
          }}
        />
      </div>
      
      {paymentStatus && (
        <div style={{ 
          marginBottom: '1rem', 
          padding: '0.5rem', 
          backgroundColor: paymentStatus.includes('warning') ? '#fff3cd' : '#d4edda',
          border: paymentStatus.includes('warning') ? '1px solid #ffeaa7' : '1px solid #c3e6cb',
          borderRadius: '4px',
          fontSize: '0.9rem'
        }}>
          {paymentStatus}
        </div>
      )}
      
      <button
        type="submit"
        className="btn btn-primary w-full"
        disabled={!stripe || isProcessing}
        style={{ fontSize: '1.1rem', padding: '1rem', marginTop: '1rem' }}
      >
        {isProcessing ? (
          <div className="d-flex align-center justify-center" style={{ gap: '0.5rem' }}>
            <div className="loading-spinner" style={{ margin: '0', width: '20px', height: '20px', borderWidth: '2px' }}></div>
            <span>Processing Payment...</span>
          </div>
        ) : (
          `Pay $${total.toFixed(2)}`
        )}
      </button>
    </form>
  );
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const {
    items,
    getSubtotal,
    getServiceFee,
    getTotal,
    clearCart
  } = useCart();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postcode: '',
    deliveryInstructions: ''
  });

  const [orderType, setOrderType] = useState('pickup'); // 'pickup' or 'delivery'
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);


  // Redirect if cart is empty
  useEffect(() => {
    if (items.length === 0) {
      navigate('/menu');
    }
  }, [items, navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      // Create order in Firebase Realtime Database
      console.log('Creating order in Firebase Realtime Database...');
      console.log('Payment Intent ID:', paymentIntent.id);
      console.log('Subtotal:', getSubtotal());
      console.log('Total:', getTotal());
      console.log('Order Type:', orderType);
      console.log('Form Data:', formData);
      console.log('Items:', items);

      const orderData = {
        paymentIntentId: paymentIntent.id,
        amount: getTotal(),
        subtotal: getSubtotal(),
        serviceFee: getServiceFee(),
        customerInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          orderType: orderType
        },
        items: items
      };

      const order = await createOrderInFirebase(orderData);

      console.log('Order created successfully with ID:', order.id);
      
      // Prepare order data for confirmation page
      const confirmationOrder = {
        id: order.id,
        orderNumber: order.orderNumber,
        timestamp: order.timestamp,
        amount: order.amount,
        serviceFee: order.serviceFee,
        customerInfo: order.customerInfo,
        items: order.items,
        orderType: order.orderType
      };

      clearCart();
      
      // Navigate to order confirmation page with order data
      navigate('/order-confirmation', { 
        state: { order: confirmationOrder } 
      });
    } catch (error) {
      console.error('Error creating order in Firebase:', error);
      alert('Payment successful, but order creation failed. Please try again or contact support.');
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    alert('Payment failed. Please try again.');
  };



  if (items.length === 0) {
    return null; // Will redirect to menu
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="checkout-page">
              {/* Hero Section */}
      <section className="hero section-full-width" style={{
          backgroundImage: 'url(/images/nagisceyloncatering-hero-550x440-550x440.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}>
          <div className="hero-content">
            <h1>Checkout</h1>
            <p>
              Complete your order and enjoy authentic Sri Lankan cuisine delivered to your door.
            </p>
          </div>
        </section>

      <section className="section">
        <div className="d-grid" style={{ gridTemplateColumns: '2fr 1fr', gap: '3rem' }}>
          {/* Left Column - Order Form */}
          <div>
            <div className="card p-4">
              <h2>Order Details</h2>
              
              {/* Order Items */}
              <div className="mb-4">
                <h3>Your Order</h3>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {items.map((item) => (
                    <div key={item.id} className="cart-item d-flex align-center mb-2" style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>
                      <img
                        src={item.image}
                        alt={item.name}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          marginRight: '1rem'
                        }}
                        onError={(e) => {
                          e.target.src = placeholderImage;
                        }}
                      />
                      <div className="cart-item-details" style={{ flex: 1 }}>
                        <h4 className="cart-item-title" style={{ margin: '0 0 0.25rem 0', fontSize: '1rem' }}>{item.name}</h4>
                        <p className="cart-item-price" style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                          Qty: {item.quantity} × ${item.price.toFixed(2)}
                        </p>
                      </div>
                      <div style={{ fontWeight: '600' }}>
                        ${(item.price * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile Order Summary - Only visible on mobile */}
              <div className="mobile-order-summary mb-4">
                <div className="card p-3" style={{ backgroundColor: '#f8f9fa' }}>
                  <h3>Order Summary</h3>
                  
                  <div className="mb-3">
                    <div className="d-flex justify-between mb-2">
                      <span>Subtotal ({items.length} items):</span>
                      <span>${getSubtotal().toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-between mb-2">
                      <span>Service Fee:</span>
                      <span>${Math.min(getServiceFee(), 3).toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-between mb-2" style={{ borderTop: '1px solid #eee', paddingTop: '0.5rem' }}>
                      <span style={{ fontWeight: '600' }}>Total:</span>
                      <span style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                        ${(getSubtotal() + Math.min(getServiceFee(), 3)).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <h4>{orderType === 'pickup' ? 'Pickup Information' : 'Delivery Information'}</h4>
                    <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.9rem' }}>
                      {orderType === 'pickup' ? (
                        <>
                          <li style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                            Collect from restaurant
                          </li>
                          <li style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                            Orders ready in 20-30 minutes
                          </li>
                          <li style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                            1A Whyalla St, Willetton, WA 6155
                          </li>
                        </>
                      ) : (
                        <>
                          <li style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                            Delivery via UberEats/DoorDash
                          </li>
                          <li style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                            Orders ready in 20-30 minutes
                          </li>
                          <li style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                            Contactless delivery available
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Order Type Selection */}
              <div className="mb-4">
                <h3>Order Type</h3>
                <div className="order-type-selection">
                  <div className="d-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '1rem',
                      border: orderType === 'pickup' ? '2px solid #ff6b35' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: orderType === 'pickup' ? '#fff5f2' : 'white',
                      transition: 'all 0.3s ease'
                    }}>
                      <input
                        type="radio"
                        name="orderType"
                        value="pickup"
                        checked={orderType === 'pickup'}
                        onChange={(e) => setOrderType(e.target.value)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Pickup</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>Collect from restaurant</div>
                      </div>
                    </label>
                    
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '1rem',
                      border: orderType === 'delivery' ? '2px solid #ff6b35' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: orderType === 'delivery' ? '#fff5f2' : 'white',
                      transition: 'all 0.3s ease'
                    }}>
                      <input
                        type="radio"
                        name="orderType"
                        value="delivery"
                        checked={orderType === 'delivery'}
                        onChange={(e) => setOrderType(e.target.value)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Delivery</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>Via UberEats/DoorDash</div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>



              {/* Customer Information */}
              <div>
                <h3>Customer Information</h3>
                
                <div className="d-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="First Name"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last Name"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email Address"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    marginBottom: '1rem'
                  }}
                />

                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    marginBottom: '1rem'
                  }}
                />

                {/* Delivery Address - Only show for delivery orders */}
                {orderType === 'delivery' && (
                  <>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Delivery Address"
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        marginBottom: '1rem'
                      }}
                    />

                    <div className="d-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        placeholder="Postcode"
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  </>
                )}

                {/* Delivery Instructions - Only show for delivery orders */}
                {orderType === 'delivery' && (
                  <>
                    <textarea
                      name="deliveryInstructions"
                      value={formData.deliveryInstructions}
                      onChange={handleInputChange}
                      placeholder="Delivery Instructions (Optional)"
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        marginBottom: '1rem',
                        resize: 'vertical'
                      }}
                    />
                  </>
                )}



                {/* Payment Method Selection */}
                <h3>Payment Method</h3>
                <div className="payment-method mb-3">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={paymentMethod === 'card'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <FaCreditCard style={{ marginRight: '0.5rem', color: '#ff6b35' }} />
                    Credit/Debit Card
                  </label>
                </div>

                {/* Stripe Payment Form */}
                <div className="card p-3 mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex align-center mb-2">
                    <FaLock style={{ color: '#28a745', marginRight: '0.5rem' }} />
                    <span style={{ color: '#28a745', fontWeight: '600' }}>Secure Payment</span>
                  </div>
                  <PaymentForm
                    subtotal={getSubtotal()}
                    total={getTotal()}
                    formData={formData}
                    items={items}
                    orderType={orderType}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    isProcessing={isProcessing}
                    setIsProcessing={setIsProcessing}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="order-summary">
            <div className="card p-4" style={{ position: 'sticky', top: '100px' }}>
              <h3>Order Summary</h3>
              
              <div className="mb-4">
                <div className="d-flex justify-between mb-2">
                  <span>Subtotal ({items.length} items):</span>
                  <span>${getSubtotal().toFixed(2)}</span>
                </div>
                <div className="d-flex justify-between mb-2">
                  <span>Service Fee:</span>
                  <span>${Math.min(getServiceFee(), 3).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-between mb-2" style={{ borderTop: '1px solid #eee', paddingTop: '0.5rem' }}>
                  <span style={{ fontWeight: '600' }}>Total:</span>
                  <span style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                    ${(getSubtotal() + Math.min(getServiceFee(), 3)).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="delivery-info mb-4">
                <h4>{orderType === 'pickup' ? 'Pickup Information' : 'Delivery Information'}</h4>
                <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.9rem' }}>
                  {orderType === 'pickup' ? (
                    <>
                      <li style={{ marginBottom: '0.5rem' }}>
                        <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                        Collect from restaurant
                      </li>
                      <li style={{ marginBottom: '0.5rem' }}>
                        <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                        Orders ready in 20-30 minutes
                      </li>
                      <li style={{ marginBottom: '0.5rem' }}>
                        <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                        1A Whyalla St, Willetton, WA 6155
                      </li>
                    </>
                  ) : (
                    <>
                      <li style={{ marginBottom: '0.5rem' }}>
                        <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                        Delivery via UberEats/DoorDash
                      </li>
                      <li style={{ marginBottom: '0.5rem' }}>
                        <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                        Orders ready in 20-30 minutes
                      </li>
                      <li style={{ marginBottom: '0.5rem' }}>
                        <span style={{ color: '#ff6b35', marginRight: '0.5rem' }}>•</span>
                        Contactless delivery available
                      </li>
                    </>
                  )}
                </ul>
              </div>

              <div className="help-section card p-3" style={{ backgroundColor: '#f8f9fa' }}>
                <h4>Need Help?</h4>
                <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Contact us if you have any questions about your order.
                </p>
                <div className="d-flex align-center mb-2">
                  <FaPhone style={{ color: '#ff6b35', marginRight: '0.5rem' }} />
                  <span>(08) 6252 8222</span>
                </div>
                <div className="d-flex align-center">
                  <FaEnvelope style={{ color: '#ff6b35', marginRight: '0.5rem' }} />
                  <span>info@nagisceylon.com.au</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </Elements>
  );
};

export default CheckoutPage; 